/**
 * CAP map marker: an explicit "CAP" badge, plus a wedge showing the
 * estimated direction of its sector antenna (GET /tree-mgmt/cap-azimuths).
 *
 * The badge is a small AdvancedMarker so it only takes clicks on itself.
 * The wedge is a separate, non-clickable map polygon in geographic
 * coordinates, so it turns with the map when the map is rotated and never
 * covers nearby lamp markers.
 */

export const CAP_BADGE_SIZE = 30;
// AdvancedMarkerElement anchors content at its bottom center; shifting the
// badge down by half its height puts its center on the CAP position, where
// the wedge starts.
export const CAP_MARKER_CENTER_TRANSFORM = 'translateY(50%)';

export const CAP_DIRECTION_COLOR = '#1565c0';
// Wedge length on screen, kept constant across zoom levels.
export const CAP_WEDGE_LENGTH_PX = 40;
// CAP sector antenna coverage (3 dB beamwidth); the wedge draws it as is.
export const CAP_ANTENNA_BEAMWIDTH_DEG = 80;

const EARTH_CIRCUMFERENCE_M = 40075016.686;

const normDeg = (v) => ((v % 360) + 360) % 360;
const finiteOrNull = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * Normalise one CAP entry from the endpoint (GET list item, or the PUT /
 * DELETE response). azimuthDeg is what to draw: the user's value when set,
 * else the estimate; null when there is neither (status says why).
 */
export function capAzimuthEntryFromApi(cap) {
  if (!cap) return null;
  const azimuth = finiteOrNull(cap.azimuth_deg);
  const isUser = cap.source === 'user' && azimuth != null;
  const estimated = finiteOrNull(cap.estimated_azimuth_deg);
  return {
    status: cap.status || null,
    azimuthDeg: azimuth == null ? null : normDeg(azimuth),
    source: isUser ? 'user' : 'estimated',
    estimatedAzimuthDeg: estimated == null ? null : normDeg(estimated),
    estimateLowConfidence: cap.status !== 'ok',
    userSetAt: cap.user_set_at || null,
    userSetBy: cap.user_set_by || null,
    spreadDeg: finiteOrNull(cap.spread_deg),
    confidence: finiteOrNull(cap.confidence) ?? 0,
    samples: Number(cap.samples) || 0,
    basis: cap.basis || null,
    // A user-set direction is drawn solid; only estimates can be low confidence.
    lowConfidence: !isUser && cap.status !== 'ok',
  };
}

/**
 * Index the endpoint payload by lower-cased serial. Every CAP is kept;
 * those with azimuthDeg null draw without a wedge.
 */
export function capAzimuthMapFromResponse(response) {
  const out = new Map();
  const caps = Array.isArray(response?.caps) ? response.caps : [];
  caps.forEach((cap) => {
    const serial = (cap?.serial || '').toString().trim().toLowerCase();
    const entry = serial ? capAzimuthEntryFromApi(cap) : null;
    if (entry) out.set(serial, entry);
  });
  return out;
}

/** Return a copy of the map with one CAP's entry replaced from a PUT / DELETE response. */
export function withCapAzimuthEntry(bySerial, cap) {
  const next = new Map(bySerial);
  const serial = (cap?.serial || '').toString().trim().toLowerCase();
  if (!serial) return next;
  const entry = capAzimuthEntryFromApi(cap);
  if (entry) next.set(serial, entry);
  else next.delete(serial);
  return next;
}

/** Parse a typed azimuth; returns degrees in [0, 360) or null if invalid. */
export function parseAzimuthInput(value) {
  const text = String(value ?? '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0 || n > 360) return null;
  return Math.round(normDeg(n) * 10) / 10;
}

/** Ground metres per screen pixel for Web Mercator at this latitude/zoom. */
export function metersPerPixel(lat, zoom) {
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (256 * 2 ** zoom);
}

/**
 * Sector outline as map coordinates. Headings are compass bearings
 * (0 = north, clockwise), which is what Google's computeOffset takes, so
 * the wedge is anchored to the ground and follows map rotation.
 * @param {{lat:number,lng:number}} center
 * @param {(from, distanceM, headingDeg) => {lat:number,lng:number}} computeOffset
 */
export function capWedgeLatLngs(center, azimuthDeg, halfAngleDeg, radiusM, computeOffset, steps = 12) {
  const points = [center];
  for (let i = 0; i <= steps; i += 1) {
    const heading = azimuthDeg - halfAngleDeg + (2 * halfAngleDeg * i) / steps;
    points.push(computeOffset(center, radiusM, heading));
  }
  return points;
}

/** Polygon / boresight-line styling; low confidence is fainter and dashed. */
export function capWedgeStyle(azimuth) {
  const low = Boolean(azimuth?.lowConfidence);
  return {
    polygon: {
      fillColor: CAP_DIRECTION_COLOR,
      fillOpacity: low ? 0.1 : 0.25,
      strokeColor: CAP_DIRECTION_COLOR,
      strokeOpacity: low ? 0.5 : 0.9,
      strokeWeight: 1,
      clickable: false,
      zIndex: 50,
    },
    boresightDashed: low,
  };
}

/**
 * Build the CAP badge SVG string.
 * @param {object} opts
 * @param {string} opts.fill badge fill (online/offline or overlay color)
 * @param {string} opts.border badge border color
 * @param {string|null} [opts.highlightColor] extra ring (selection, access lists, heard-by)
 * @param {number} [opts.highlightWidth] ring width from the caller's emphasis level
 * @param {string|null} [opts.notesBadgeColor] amber dot when the CAP has notes
 */
export function buildCapBadgeSvg({
  fill,
  border,
  highlightColor = null,
  highlightWidth = 2,
  notesBadgeColor = null,
}) {
  const size = CAP_BADGE_SIZE;
  const ringWidth = Math.max(1, Number(highlightWidth) || 2);
  const inset = ringWidth / 2 + 0.5;
  const ring = highlightColor
    ? `<rect x="${inset}" y="${inset}" width="${size - 2 * inset}" height="${size - 2 * inset}" rx="7" fill="none" stroke="${highlightColor}" stroke-width="${ringWidth}" opacity="0.85"/>`
    : '';
  const notes = notesBadgeColor
    ? `<circle cx="${size - 5}" cy="5" r="3.6" fill="${notesBadgeColor}" stroke="white" stroke-width="1.2"/>`
    : '';
  const pad = highlightColor ? ringWidth + 2 : 2;
  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <rect x="${pad}" y="${pad}" width="${size - 2 * pad}" height="${size - 2 * pad}" rx="5" fill="${fill}" stroke="${border}" stroke-width="2"/>
      <text x="${size / 2}" y="${size / 2 + 3.2}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="9" font-weight="700" fill="white">CAP</text>
      ${ring}
      ${notes}
    </svg>`;
}

/** One tooltip line describing the direction, or null when there is none. */
/** Why a CAP has no direction, for the popup (status from the endpoint). */
export function capNoDirectionReason(azimuth) {
  switch (azimuth?.status) {
    case 'no_gps':
      return 'No direction: this CAP has no GPS location. Set its location first, or set the direction by hand.';
    case 'insufficient_data':
      return 'No direction yet: fewer than 3 lamps with GPS hear this CAP.';
    case 'no_direction':
      return 'No direction: lamps hear this CAP about equally all around. Set the direction by hand.';
    default:
      return 'Direction not available right now.';
  }
}

export function capAzimuthTitle(azimuth) {
  if (!azimuth || azimuth.azimuthDeg == null) return null;
  const deg = Math.round(azimuth.azimuthDeg);
  if (azimuth.source === 'user') {
    const by = azimuth.userSetBy ? ` by ${azimuth.userSetBy}` : '';
    const est = azimuth.estimatedAzimuthDeg != null ? `; estimate ${Math.round(azimuth.estimatedAzimuthDeg)}°` : '';
    return `Antenna direction ${deg}° (set${by}${est}); wedge shows the ${CAP_ANTENNA_BEAMWIDTH_DEG}° coverage`;
  }
  if (azimuth.basis === 'signal_guess') {
    return `Antenna direction ≈ ${deg}° (guess: the ${CAP_ANTENNA_BEAMWIDTH_DEG}° beam covering most of the ${azimuth.samples} lamps that hear it; no clear signal pattern)`;
  }
  const quality = azimuth.lowConfidence ? 'low confidence' : `confidence ${Math.round(azimuth.confidence * 100)}%`;
  const spread = Number.isFinite(azimuth.spreadDeg) ? ` ±${Math.max(1, Math.round(azimuth.spreadDeg))}°` : '';
  return `Antenna direction ≈ ${deg}°${spread} (estimated from ${azimuth.samples} lamps, ${quality}); wedge shows the ${CAP_ANTENNA_BEAMWIDTH_DEG}° coverage`;
}
